import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";

export default function ForgotPasswordPage() {
  const { sendResetLink } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    await sendResetLink(email.trim());
    setLoading(false);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="flex min-h-screen bg-white">
        <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative bg-sidebar-bg overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-sidebar-bg via-sidebar-bg to-sidebar-hover" />
          <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.6)_0%,transparent_60%)]" />
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-indigo-600/10 blur-3xl" />
          <div className="relative z-10 flex flex-col justify-center w-full p-14 xl:p-20">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
                <i className="ri-pie-chart-2-fill text-xl text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-white">CRM Pro</span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[420px] text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <i className="ri-mail-check-line text-2xl text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Check your inbox</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-8">
              If an account exists for{" "}
              <span className="font-medium text-gray-700">{email}</span>, we've sent a password
              reset link. Please check your email and follow the instructions.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative bg-sidebar-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sidebar-bg via-sidebar-bg to-sidebar-hover" />
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.6)_0%,transparent_60%)]" />
        <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-indigo-600/10 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-center w-full p-14 xl:p-20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <i className="ri-pie-chart-2-fill text-xl text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-white">CRM Pro</span>
          </div>
          <p className="text-gray-400 text-sm mt-6 max-w-xs">
            No worries — we'll get you back into your account in no time.
          </p>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[420px]">
          <div className="lg:hidden flex flex-col items-center mb-10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
                <i className="ri-pie-chart-2-fill text-lg text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">CRM Pro</span>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Forgot password?</h1>
            <p className="text-sm text-gray-500 mt-1.5">
              Enter your email and we'll send you a reset link.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-sm mb-6">
              <i className="ri-error-warning-fill text-base" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                "Send reset link"
              )}
            </button>
          </form>

          <div className="text-center mt-8">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}